import { Link, useLocation } from "react-router-dom";
import styled, { css, useTheme } from "styled-components";
import {
  House, BookOpen, Headphones, Notebook, SpeakerHigh, Clock, Gear, X
} from "@phosphor-icons/react";

type Dir = "rtl" | "ltr";

const NAV = [
  { href: "/",             labelAr: "الصفحة الرئيسية", labelEn: "Home",            icon: House },
  { href: "/surah",        labelAr: "المصحف الشريف",   labelEn: "Holy Quran",       icon: BookOpen },
  { href: "/listen",       labelAr: "الاستماع للقرآن", labelEn: "Listen to Quran",  icon: Headphones },
  { href: "/mafatih",      labelAr: "مفاتيح الجنان",    labelEn: "Mafatih al-Jinan", icon: Notebook },
  { href: "/duas",         labelAr: "الاستماع للأدعية", labelEn: "Listen to Duas",   icon: SpeakerHigh },
  { href: "/prayer-times", labelAr: "مواقيت الصلاة",    labelEn: "Prayer Times",     icon: Clock },
  { href: "/settings",     labelAr: "الإعدادات",        labelEn: "Settings",         icon: Gear },
] as const;

export default function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { pathname } = useLocation();
  const { dir } = useTheme() as { dir: Dir };

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : (pathname ?? "").startsWith(href);

  return (
    <Nav role="navigation" aria-label="Sidebar" open={open} $dir={dir}>
      <MobileHeader>
        <Close onClick={onClose} aria-label="Close">
          <X size={20} weight="bold" />
        </Close>
        <Title>Quran</Title>
      </MobileHeader>

      <List>
        {NAV.map(({ href, labelAr, labelEn, icon: Icon }) => {
          const active = isActive(href);
          return (
            <Item key={href} $active={active} onClick={onClose}>
              <Link to={href} aria-current={active ? "page" : undefined}>
                <Icon size={20} weight={active ? "fill" : "regular"} />
                <span>{dir === "rtl" ? labelAr : labelEn}</span>
              </Link>
            </Item>
          );
        })}
      </List>
    </Nav>
  );
}

const Nav = styled.nav<{ open: boolean; $dir: Dir }>`
  background: linear-gradient(180deg, 
    var(--surface) 0%, 
    color-mix(in oklab, var(--surface) 98%, var(--background)) 100%
  );
  border-inline-end: 1px solid var(--border);
  width: 280px;
  z-index: 60;
  backdrop-filter: blur(8px);

  display: none;
  @media (min-width: 1000px) {
    display: block;
    position: sticky; top: 0;
    height: 100dvh;
    box-shadow: inset -1px 0 0 rgba(0, 0, 0, 0.02);
  }

  @media (max-width: 999px) {
    display: block;
    position: fixed;
    inset-block: 0;
    inset-inline-start: 0;
    will-change: transform;
    transition: transform .3s cubic-bezier(0.4, 0, 0.2, 1);
    && {
      transform: ${({ open, $dir }) =>
        open ? "translateX(0)" : $dir === "rtl" ? "translateX(100%)" : "translateX(-100%)"};
    }
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.15), 2px 0 8px rgba(0, 0, 0, 0.08);
  }
`;

const MobileHeader = styled.div`
  display: none;
  @media (max-width: 999px) {
    display: flex; align-items: center; gap: 12px;
    height: 64px; padding-inline: 16px;
    border-bottom: 1px solid var(--border);
    background: color-mix(in oklab, var(--surface) 95%, var(--primary));
  }
`;

const Close = styled.button`
  display: inline-grid; place-items: center;
  width: 40px; height: 40px; border-radius: 12px;
  border: 1px solid var(--border); 
  background: var(--surface); 
  color: var(--text);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: color-mix(in oklab, var(--surface) 90%, var(--text));
    transform: rotate(90deg);
  }
  
  &:active {
    transform: rotate(90deg) scale(0.95);
  }
`;

const Title = styled.div` 
  font-weight: 600; 
  font-size: 1.15rem;
  color: var(--text); 
  letter-spacing: -0.01em;
`;

const List = styled.ul`
  list-style: none; 
  padding: 16px 12px; 
  margin: 0;
  display: grid; 
  gap: 4px;
  overflow-y: auto;
  max-height: calc(100dvh - 80px);
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: color-mix(in oklab, var(--border) 60%, transparent);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: var(--border);
  }
`;

const Item = styled.li<{ $active?: boolean }>`
  a {
    position: relative;
    display: flex; 
    align-items: center; 
    gap: 12px;
    text-decoration: none;
    padding: 12px 14px;
    border-radius: 14px;
    color: var(--text);
    border: 1px solid transparent;
    background: transparent;
    font-weight: 500;
    font-size: 0.95rem;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, 
        color-mix(in oklab, var(--primary) 8%, transparent) 0%, 
        color-mix(in oklab, var(--primary) 4%, transparent) 100%
      );
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    & > * {
      position: relative;
      z-index: 1;
    }

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px color-mix(in oklab, var(--primary) 20%, transparent);
    }
    
    &:hover {
      background: color-mix(in oklab, var(--surface) 50%, var(--background));
      transform: translateX(4px);
      
      &::before {
        opacity: 1;
      }
    }

    ${({ $active }) => $active && css`
      background: linear-gradient(135deg, 
        color-mix(in oklab, var(--primary) 12%, var(--surface)) 0%, 
        color-mix(in oklab, var(--primary) 8%, var(--surface)) 100%
      );
      color: var(--primary);
      border-color: color-mix(in oklab, var(--primary) 25%, transparent);
      font-weight: 600;
      box-shadow: 0 2px 8px color-mix(in oklab, var(--primary) 15%, transparent),
                  inset 0 1px 0 rgba(255, 255, 255, 0.1);
      
      &::before {
        opacity: 0.8;
      }
      
      &:hover {
        transform: translateX(2px);
      }
    `}
  }
`;
